import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { AssessmentInfo } from "@/types/assessment";
import { Download, MoreHorizontal, PenLine, Trash2 } from "lucide-react";
import { AssessmentBadge } from "./AssessmentBadge";
import {
  calculateAssessmentPortalStatus,
  calculateAssessmentStatus,
} from "../../course-service";
import { useAppState } from "@/store/useApp";
import { AgentAvatar } from "@/components/AgentAvatar";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface AssessmentListProps {
  assessments: AssessmentInfo[];
  onEditAssessment: (assessment: AssessmentInfo) => void;
  onDeleteAssessment: (assessmentId: string) => void;
  onDownloadAssessment: (assessment: AssessmentInfo) => void;
  onPublishAssessment: (assessment: AssessmentInfo) => void;
}

const formatCreatedAt = (
  value: string | number | Date | null | undefined
): string => {
  if (!value) return "-";
  try {
    const date = new Date(value);
    if (isNaN(date.getTime())) return "-";
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return "-";
  }
};

export const AssessmentList = ({
  assessments,
  onEditAssessment,
  onDeleteAssessment,
  onDownloadAssessment,
  onPublishAssessment,
}: AssessmentListProps) => {
  const documents = useAppState((state) => state.documents || []);
  const agents = useAppState((state) => state.agents || []);

  return (
    <Table>
      <TableHeader>
        <TableRow className="border-b border-muted">
          <TableHead className="w-2/4">Title</TableHead>
          <TableHead>Created on</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Type</TableHead>
          <TableHead>Agent</TableHead>
          <TableHead>Actions</TableHead>
          <TableHead className="w-16"></TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {assessments.map((assessment) => {
          const status = calculateAssessmentStatus(assessment);
          const portalStatus = calculateAssessmentPortalStatus(assessment);
          const assessmentDocuments = documents.filter(
            (doc) => doc.assessmentId === assessment.id
          );
          const gradedCount =
            assessment.gradedCount ?? assessment.gradedCount ?? 0;
          const isOnboarding = assessment.metaType === "onboarding";
          const isPublished = portalStatus !== "pending";
          const canExport =
            !assessment.isCreating &&
            (assessmentDocuments.length > 0 || gradedCount > 0);
          const agentName =
            assessment.agentName ||
            agents.find((agent) => agent.id === assessment.agentId)?.name ||
            "Agent";
          return (
            <TableRow
              key={assessment.id}
              id={isOnboarding ? "hint-assessment" : assessment.id}
            >
              <TableCell className="font-medium">{assessment.title}</TableCell>
              <TableCell className="text-muted-foreground">
                {formatCreatedAt(assessment.createdAt)}
              </TableCell>
              <TableCell>
                <AssessmentBadge
                  isCreating={assessment.isCreating}
                  status={portalStatus}
                />
              </TableCell>
              <TableCell>
                {assessment.type
                  ? assessment.type.charAt(0).toUpperCase() +
                  assessment.type.slice(1).toLowerCase()
                  : "-"}
              </TableCell>
              <TableCell>
                {assessment.agentId ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="inline-flex">
                        <AgentAvatar agentId={assessment.agentId} size="sm" />
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>{agentName}</TooltipContent>
                  </Tooltip>
                ) : (
                  "-"
                )}
              </TableCell>
              <TableCell>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onPublishAssessment(assessment)}
                  disabled={isPublished}
                >
                  Publish
                </Button>
              </TableCell>
              <TableCell>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      onClick={() => onDownloadAssessment(assessment)}
                      disabled={!canExport}
                    >
                      <Download className="mr-2 h-4 w-4" />
                      Download
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => onEditAssessment(assessment)}
                    >
                      <PenLine className="mr-2 h-4 w-4" />
                      Rename
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => onDeleteAssessment(assessment.id)}
                      disabled={status !== "pending"}
                      variant={
                        status === "pending" ? "destructive" : "default"
                      }
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};
